'use strict';
import { hpBackgrounds } from 'Constants/backgroundImages';
import { fileNames } from 'Constants/imageUrls';
import { HOME, LISTING } from 'Constants/tracking';
import { PLATFORM } from 'Constants/device.APP_TARGET';
import { QUALITY_IMAGE_60, ASPECT_RATIO, DEFAULT_IMG_HEIGHT, MEDIAVERSE_DOMAIN_IDENTIFIER } from 'Constants/images';

const themePathMap = {
    olx: '',
    letgo: 'letgo/',
    otoplus: '',
    '321sprzedane': '321sprzedane/'
};

const themeMap = {
    olx: '',
    letgo: 'letgo/',
    otoplus: 'otoplus/',
    '321sprzedane': '321sprzedane/'
};

const avatar0 = fileNames.AVATAR_0;
const avatar1 = fileNames.AVATAR_1;
const avatar2 = fileNames.AVATAR_2;
const avatar3 = fileNames.AVATAR_3;
const avatar4 = fileNames.AVATAR_4;
const avatars = [avatar0, avatar1, avatar2, avatar3, avatar4];

const getAvatarIcon = (staticAssetUrl, avatarId = 1, theme = 'olx') => {
    let url = `${staticAssetUrl}${avatars[avatarId]}`;

    url = url.replace('/$theme$/', themePathMap[theme] || '');
    return url;
};

const getUserImage = (staticAssetUrl, user = { avatar_id: 1 }, size = '120x120', theme = 'olx') => {
    if (user.images && user.images.length && user.images[0].url) {
        return `${user.images[0].url};s=${size}`;
    }
    else if (user.image) {
        return `${user.image};s=${size}`;
    }

    return getAvatarIcon(staticAssetUrl, user.avatar_id, theme);
};

const computeImageSourceType = type => {
    switch (type) {
        case 'webp':
            return 'image/webp';
        case 'svg':
            return 'image/svg+xml';
        default:
            return 'image/jpeg';
    }
};

const convertToAbsoluteImageURI = (uri, ext = 'png', staticAsset = '', scalingparam = '1x', mode = 'light', theme = 'olx') => {
    if (!uri) {
        return null;
    }

    let url = uri;

    if (uri && staticAsset && uri.indexOf('https://') === -1) {
        url = new URL(uri, staticAsset).href;
    }

    url = url.replace('$width$', scalingparam);
    url = url.replace('$mode$', mode);
    url = url.replace('$ext$', ext);
    url = url.replace('$platform$', PLATFORM);
    url = url.replace('$scalingParam$', scalingparam);
    url = url.replace('$density$', scalingparam);

    /** create a theme based map for common assets
    * like error, login, no results images etc.
    * by default theme is 'olx' and path is ''
    * for other themes like 'letgo', 'otoplus' there are theme based folder in
    * static assets repo. This is to ensure uniformity in names
    * and prevent having theme based checks everywhere
    */
    url = url.replace('/$theme$/', themePathMap[theme] || '');
    return url;
};

const getBackgroundBanner = (config, page) => {
    const countryCode = config.get('countryCode');

    switch (page) {
        case HOME:
            return hpBackgrounds[PLATFORM][countryCode] || hpBackgrounds[PLATFORM].default;
        case LISTING:
            return config.get('listing', 'listingBannerUrl')?.[PLATFORM];
    }

    return '';
};

const getModifiedFCGImgs = ({ image, height, width, disableImgOptimization = false }) => {
    const { url } = image;
    let finalUrl = url;

    if (url.indexOf('fcg') !== -1 && !disableImgOptimization) {
        const [mainUrl, queryParams] = url.split('?');

        width = width || Math.round(ASPECT_RATIO * (height || DEFAULT_IMG_HEIGHT));
        height = height || Math.round(1 / ASPECT_RATIO * (width));

        finalUrl = `${mainUrl}/${width}/${height}?${queryParams}`;
    }
    return {
        ...image,
        url: finalUrl
    };
};

const isMediaverseImageUrl = imageUrl => {
    return imageUrl?.includes(MEDIAVERSE_DOMAIN_IDENTIFIER);
};

/*
    Note: The below written hardcoded check must be removed from the code which is used for determining
    whether the image url contains apollo or not. By this validation, the code is implementing some changes
    if the url belongs to apollo. In future, if the end point of apollo changes, then it will again be required to get changed.
    We may at-least being the value of phraseToBeChecked from environment variable or can think of a better approach.
*/
const isMediaverseImage = image => {
    return image.url && isMediaverseImageUrl(image.url);
};

const getImg = ({ image, width, height, disableImgOptimization, disableSrcSet, defaultDimension }) => {
    const isAkamaizedImage = isMediaverseImage(image);

    if (isAkamaizedImage) {
        const relation = image.width / image.height;
        const imageUrl = `${image.url}`;

        const url = defaultDimension?.length ? `${imageUrl};${defaultDimension}` : `${imageUrl};s=${width}x0`;
        /* eslint-disable no-negated-condition */
        const srcset = !disableSrcSet
            ? `${imageUrl};s=280x0;q=${ QUALITY_IMAGE_60 } 320w, 
                ${imageUrl};s=320x0;q=${ QUALITY_IMAGE_60 } 360w, 
                ${imageUrl};s=335x0;q=${ QUALITY_IMAGE_60 } 375w, 
                ${imageUrl};s=440x0;q=${ QUALITY_IMAGE_60 } 480w, 
                ${imageUrl};s=640x0;q=${ QUALITY_IMAGE_60 } 720w, 
                ${imageUrl};s=943x0;q=${ QUALITY_IMAGE_60 } 1024w, 
                ${imageUrl};s=780x0;q=${ QUALITY_IMAGE_60 } 1025w`
            : '';

        return {
            ...image,
            url,
            srcset,
            relation
        };
    }

    return getModifiedFCGImgs({ image, width, height, disableImgOptimization });
};

const convertToAbsoluteImageURIV2 = (baseUri, uri, theme = 'olx') => {
    let url = uri;

    if (uri && baseUri && uri.indexOf('https://') === -1) {
        url = `${baseUri}${uri}`;
    }

    url = url.replace('/$theme$/', themeMap[theme] || '');
    return url;
};

export {
    getUserImage,
    computeImageSourceType,
    convertToAbsoluteImageURI,
    convertToAbsoluteImageURIV2,
    getAvatarIcon,
    getBackgroundBanner,
    avatar1,
    avatar2,
    avatar3,
    avatar4,
    getImg,
    getModifiedFCGImgs,
    isMediaverseImage,
    isMediaverseImageUrl
};
